import { ReactNode } from "react";
import { LoaderCircle, LucideIcon, RefreshCw } from "lucide-react";
import { LogoAnimation } from "@/components/Logo/logoAnimation";
import { cn } from "@/lib/utils";

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

interface CardHeaderProps {
  icon?: LucideIcon;
  title: string;
  subtitle?: string;
  actions?: ReactNode;
  className?: string;
  loading?: boolean;
  notify?: boolean;
  refetch?: () => void;
}

interface CardActionsProps {
  children: ReactNode;
  className?: string;
}

export const Card = ({ children, className = "" }: CardProps) => {
  return (
    <div
      className={`flex flex-col bg-card rounded-xl border shadow-sm overflow-hidden ${className}`}
    >
      {children}
    </div>
  );
};

export const CardHeader = ({
  icon: Icon,
  title,
  subtitle,
  actions,
  className = "",
  loading,
  notify,
  refetch,
}: CardHeaderProps) => {
  return (
    <div className={`p-1.5 border-b flex items-center gap-2 h-12 ${className}`}>
      {Icon && (
        <div
          onClick={() => refetch?.()}
          className={cn(
            "size-9 rounded-xl bg-blue-50 dark:bg-blue-950/30 flex items-center justify-center flex-shrink-0",
            { "group/icon hover:relative hover:cursor-pointer": !!refetch }
          )}
        >
          {loading ? (
            <LogoAnimation size={18} variant="pulse" className="bg-blue-600" />
          ) : notify ? (
            <div className="relative flex items-center">
              <span className="absolute inline-flex h-3 w-3 rounded-full bg-red-500 opacity-75 animate-ping"></span>
              <span className="relative inline-flex h-3 w-3 rounded-full bg-red-500"></span>
            </div>
          ) : (
            <Icon
              className={cn(
                "size-4 group-hover/icon:hidden text-blue-600 dark:text-blue-400"
              )}
              strokeWidth={2.5}
            />
          )}
          {!!refetch && !loading && (
            <div className="size-full group-hover/icon:flex items-center justify-center hidden bg-transparent absolute top-0 left-0 z-10">
              <RefreshCw className="size-4 text-blue-600 dark:text-blue-400" />
            </div>
          )}
        </div>
      )}

      <div className={`flex-1 min-w-0 ${subtitle ? "" : "flex items-center"}`}>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold truncate">{title}</h3>
          {subtitle && (
            <p className="text-xs text-muted-foreground truncate">{subtitle}</p>
          )}
        </div>
      </div>
      {actions && (
        <div className="flex items-center gap-1 flex-shrink-0">{actions}</div>
      )}
    </div>
  );
};

export const CardContent = ({ children, ...rest }: CardProps) => {
  return <div className={cn("flex-1 p-3", rest.className)}>{children}</div>;
};

export const CardActions = ({ children, className = "" }: CardActionsProps) => {
  return <div className={`px-3 py-2 border-t ${className}`}>{children}</div>;
};
